import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Plus, ExternalLink, Loader2, Building2, AlertCircle, ArrowUpDown, ArrowUp, ArrowDown, Filter, X, ChevronDown } from 'lucide-react';
import { getAccessToken } from '@lib/msal';
import { CanEdit, ReadOnlyBadge } from '@components/common/RequirePermission';
import OrgDetailPanel, { OrgDetail } from '@components/people/OrgDetailPanel';
import { fetchOrgDetail } from '@lib/beacon';

interface Organisation {
  id: number;
  name: string;
  type: string | null;
  lastModified: string | null;
}

interface SearchResponse {
  data: Organisation[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
  beaconAccountId?: string;
  message?: string;
}

type SortField = 'name' | 'updated_at';
type SortDirection = 'asc' | 'desc';

const MIN_SEARCH_LENGTH = 3;
const PAGE_SIZE = 20;

export default function Organisations() {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [results, setResults] = useState<Organisation[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [beaconAccountId, setBeaconAccountId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [sortBy, setSortBy] = useState<SortField>('updated_at');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');

  // Type filter state
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const typeDropdownRef = useRef<HTMLDivElement>(null);

  // Org detail panel state
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [selectedOrgName, setSelectedOrgName] = useState<string | undefined>(undefined);
  const [orgDetail, setOrgDetail] = useState<OrgDetail | null>(null);
  const [orgDetailLoading, setOrgDetailLoading] = useState(false);
  const [orgDetailError, setOrgDetailError] = useState<string | null>(null);

  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // Fetch available organisation types on mount
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const apiBase = (import.meta.env.VITE_API_URL || '').replace(/\/api\/?$/, '');
        const token = await getAccessToken();
        const response = await fetch(`${apiBase}/api/beacon/organisations/types`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (response.ok) {
          const data = await response.json();
          // API returns { types: [...] }
          setAvailableTypes(data.types || data.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch organisation types:', err);
      }
    };
    fetchTypes();
  }, []);

  // Close type dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target as Node)) {
        setIsTypeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset results when search or filters change
  useEffect(() => {
    setResults([]);
    setPage(1);
    setHasMore(false);
  }, [debouncedSearch, sortBy, sortDir, selectedTypes]);

  // Perform search when debounced value changes
  const performSearch = useCallback(async (query: string, pageNum: number, append: boolean = false, types: string[] = []) => {
    // Need either search term or type filter
    if (query.length < MIN_SEARCH_LENGTH && types.length === 0) {
      setResults([]);
      setTotal(0);
      setHasSearched(false);
      setHasMore(false);
      return;
    }

    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    setError(null);
    setHasSearched(true);

    try {
      const apiBase = (import.meta.env.VITE_API_URL || '').replace(/\/api\/?$/, '');
      const params = new URLSearchParams({
        limit: PAGE_SIZE.toString(),
        page: pageNum.toString(),
        sort_by: sortBy,
        sort_dir: sortDir,
      });

      // Add search if provided
      if (query.length >= MIN_SEARCH_LENGTH) {
        params.set('search', query);
      }

      // Add type filter if selected
      if (types.length > 0) {
        params.set('types', types.join(','));
      }

      const token = await getAccessToken();
      const response = await fetch(`${apiBase}/api/beacon/organisations?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error('Failed to search organisations');
      }

      const data: SearchResponse = await response.json();

      if (append) {
        setResults(prev => [...prev, ...data.data]);
      } else {
        setResults(data.data);
      }

      setTotal(data.pagination.total);
      setHasMore(data.pagination.hasMore);

      if (data.beaconAccountId) {
        setBeaconAccountId(data.beaconAccountId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
      if (!append) {
        setResults([]);
        setTotal(0);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [sortBy, sortDir]);

  // Initial search
  useEffect(() => {
    performSearch(debouncedSearch, 1, false, selectedTypes);
  }, [debouncedSearch, sortBy, sortDir, selectedTypes, performSearch]);

  // Infinite scroll observer
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting && hasMore && !isLoadingMore && !isLoading) {
          const nextPage = page + 1;
          setPage(nextPage);
          performSearch(debouncedSearch, nextPage, true, selectedTypes);
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, isLoadingMore, isLoading, page, debouncedSearch, performSearch]);

  // Open the org detail panel and fetch data
  const openOrgDetail = (org: Organisation) => {
    setSelectedOrgId(org.id);
    setSelectedOrgName(org.name);
    setOrgDetail(null);
    setOrgDetailError(null);
    setOrgDetailLoading(true);

    fetchOrgDetail(org.id)
      .then(data => {
        setOrgDetail(data);
      })
      .catch(err => {
        setOrgDetailError(err instanceof Error ? err.message : 'Failed to load details');
      })
      .finally(() => {
        setOrgDetailLoading(false);
      });
  };

  const closeOrgDetail = () => {
    setSelectedOrgId(null);
    setOrgDetail(null);
    setOrgDetailError(null);
  };

  // Handle column sort
  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  // Toggle type selection
  const toggleType = (type: string) => {
    setSelectedTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  // Clear all type filters
  const clearTypeFilters = () => {
    setSelectedTypes([]);
  };

  // Get sort icon for column
  const getSortIcon = (field: SortField) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortDir === 'asc'
      ? <ArrowUp className="h-4 w-4 text-sca-purple-600" />
      : <ArrowDown className="h-4 w-4 text-sca-purple-600" />;
  };

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const showMinCharsHint = searchTerm.length > 0 && searchTerm.length < MIN_SEARCH_LENGTH && selectedTypes.length === 0;

  return (
    <div className="space-y-6">
      {/* Header actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex-1 flex gap-3">
          {/* Search input */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="search"
                placeholder="Search organisations by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10"
                autoFocus
              />
              {isLoading && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
              )}
            </div>
            {showMinCharsHint && (
              <p className="text-xs text-gray-500 mt-1">
                Type at least {MIN_SEARCH_LENGTH} characters to search
              </p>
            )}
          </div>

          {/* Type filter dropdown */}
          {availableTypes.length > 0 && (
            <div className="relative" ref={typeDropdownRef}>
              <button
                onClick={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}
                className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                  selectedTypes.length > 0
                    ? 'border-sca-purple-500 bg-sca-purple-50 text-sca-purple-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Filter className="h-4 w-4" />
                Type
                {selectedTypes.length > 0 && (
                  <span className="bg-sca-purple-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                    {selectedTypes.length}
                  </span>
                )}
                <ChevronDown className={`h-4 w-4 transition-transform ${isTypeDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isTypeDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-20 max-h-80 overflow-y-auto">
                  <div className="p-2 border-b border-gray-100 flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500 uppercase">Filter by Type</span>
                    {selectedTypes.length > 0 && (
                      <button
                        onClick={clearTypeFilters}
                        className="text-xs text-sca-purple-600 hover:text-sca-purple-700"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                  <div className="p-2">
                    {availableTypes.map((type) => (
                      <label
                        key={type}
                        className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedTypes.includes(type)}
                          onChange={() => toggleType(type)}
                          className="h-4 w-4 rounded border-gray-300 text-sca-purple-500 focus:ring-sca-purple-500"
                        />
                        <span className="text-sm text-gray-700">{type}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-3 items-center">
          <ReadOnlyBadge />
          <CanEdit>
            <a
              href={`https://app.beaconcrm.org/account/${beaconAccountId || ''}/records/organization/list`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add in Beacon
              <ExternalLink className="h-3 w-3 ml-1" />
            </a>
          </CanEdit>
        </div>
      </div>

      {/* Active type filters */}
      {selectedTypes.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedTypes.map((type) => (
            <span
              key={type}
              className="inline-flex items-center gap-1 px-2.5 py-1 bg-sca-purple-100 text-sca-purple-700 text-sm rounded-full"
            >
              {type}
              <button
                onClick={() => toggleType(type)}
                className="hover:bg-sca-purple-200 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">Search failed</p>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      )}

      {/* Results count */}
      {hasSearched && !isLoading && !error && (
        <p className="text-sm text-gray-500">
          {total === 0
            ? `No organisations found matching "${debouncedSearch}"${selectedTypes.length > 0 ? ` with selected types` : ''}`
            : `Found ${total} ${total === 1 ? 'organisation' : 'organisations'} matching "${debouncedSearch}"${selectedTypes.length > 0 ? ` (filtered by type)` : ''}`}
        </p>
      )}

      {/* Results table */}
      {results.length > 0 && (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-2">
                      Name
                      {getSortIcon('name')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('updated_at')}
                  >
                    <div className="flex items-center gap-2">
                      Last Modified
                      {getSortIcon('updated_at')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {results.map((org) => (
                  <tr
                    key={org.id}
                    onClick={() => openOrgDetail(org)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0 rounded-lg bg-sca-purple-50 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-sca-purple-500" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{org.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {org.type ? (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          {org.type}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(org.lastModified)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <ExternalLink className="h-4 w-4 text-gray-400" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Load more indicator */}
          <div ref={loadMoreRef} className="h-10 flex items-center justify-center">
            {isLoadingMore && (
              <Loader2 className="h-5 w-5 text-sca-purple-500 animate-spin" />
            )}
          </div>
        </div>
      )}

      {/* Empty state - no search yet */}
      {!hasSearched && !isLoading && (
        <div className="text-center py-16 card">
          <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Search for Organisations</h3>
          <p className="text-gray-500 max-w-sm mx-auto">
            Enter at least {MIN_SEARCH_LENGTH} characters to search for organisations in Beacon CRM.
            You can also filter by type.
          </p>
        </div>
      )}

      {/* Empty state - no results */}
      {hasSearched && !isLoading && results.length === 0 && !error && (
        <div className="text-center py-16 card">
          <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Organisations Found</h3>
          <p className="text-gray-500 max-w-sm mx-auto">
            No organisations match your search "{debouncedSearch}"{selectedTypes.length > 0 ? ' with the selected type filters' : ''}.
            Try a different search term or clear the filters.
          </p>
          <CanEdit>
            <a
              href={`https://app.beaconcrm.org/account/${beaconAccountId || ''}/records/organization/list`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-4 text-sca-purple-600 hover:text-sca-purple-700"
            >
              <Plus className="h-4 w-4" />
              Add organisation in Beacon
              <ExternalLink className="h-3 w-3" />
            </a>
          </CanEdit>
        </div>
      )}

      {/* Integration note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          <strong>Note:</strong> Search results are fetched directly from Beacon CRM.
          Click any row to view contact details, or open the full record in{' '}
          <a
            href="https://app.beaconcrm.org"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:no-underline"
          >
            Beacon CRM
          </a>
          .
        </p>
      </div>

      {/* Org detail slide-over panel */}
      <OrgDetailPanel
        orgId={selectedOrgId}
        orgName={selectedOrgName}
        detail={orgDetail}
        isLoading={orgDetailLoading}
        error={orgDetailError}
        onClose={closeOrgDetail}
      />
    </div>
  );
}
